/**
 * The 9x9 grid.
 *
 * Built once, then updated in place -- 81 cells re-created on every keystroke
 * would throw away focus, which on a keyboard-navigated grid means throwing
 * away the player's place.
 *
 * Accessibility notes that are requirements, not polish:
 *  - It is a real ARIA grid with a roving tabindex, so it is reachable and
 *    navigable without a mouse.
 *  - Every state cue has a shape as well as a colour (WCAG 1.4.1). Givens are
 *    heavier than entries, the selected cell has the thickest ring, same-digit
 *    cells have a thinner one, conflicts are underlined, and mistakes carry a
 *    corner wedge. Screenshot the grid in greyscale: nothing should vanish.
 */

import { CELLS, SIZE, EMPTY, cellAt, colOf, rowOf, type Digit } from "../core/grid";
import { CLASSIC } from "../core/constraints";
import { digitsOf, maskOf } from "../core/candidates";
import type { Game } from "../state/game";
import type { Settings } from "../state/settings";

export interface BoardHighlight {
  /** Cells forming the current hint's pattern. */
  readonly pattern?: ReadonlySet<number>;
  /** Cells the current hint acts on. */
  readonly focus?: ReadonlySet<number>;
}

export interface BoardSelection {
  readonly cell: number | null;
  /** The locked digit in digit-first mode. */
  readonly digit: Digit | null;
}

interface CellParts {
  readonly root: HTMLDivElement;
  readonly value: HTMLSpanElement;
  readonly corner: HTMLSpanElement;
  readonly center: HTMLSpanElement;
}

export class BoardView {
  readonly element: HTMLDivElement;
  private readonly cells: CellParts[] = [];

  constructor(onSelect: (cell: number) => void) {
    this.element = document.createElement("div");
    this.element.className = "board";
    this.element.setAttribute("role", "grid");
    this.element.setAttribute("aria-label", "Sudoku grid");

    for (let row = 0; row < SIZE; row++) {
      const rowEl = document.createElement("div");
      rowEl.className = "board-row";
      rowEl.setAttribute("role", "row");

      for (let col = 0; col < SIZE; col++) {
        const cell = cellAt(row, col);
        const root = document.createElement("div");
        root.className = "cell";
        root.setAttribute("role", "gridcell");
        root.tabIndex = -1;
        root.dataset.cell = String(cell);

        const corner = document.createElement("span");
        corner.className = "marks marks-corner";
        const center = document.createElement("span");
        center.className = "marks marks-center";
        const value = document.createElement("span");
        value.className = "cell-value";

        root.append(corner, center, value);
        root.addEventListener("pointerdown", (event) => {
          event.preventDefault();
          onSelect(cell);
        });

        rowEl.append(root);
        this.cells.push({ root, value, corner, center });
      }
      this.element.append(rowEl);
    }
  }

  focusSelected(cell: number): void {
    this.cells[cell]?.root.focus({ preventScroll: true });
  }

  render(
    game: Game,
    settings: Settings,
    selection: BoardSelection,
    highlight: BoardHighlight = {},
  ): void {
    const conflicts = game.conflicts();
    const mistakes = settings.checkMistakes ? game.mistakes() : new Set<number>();
    const auto = settings.autoCandidates ? game.autoCandidates() : null;

    // What counts as "the digit of interest": an explicitly locked digit in
    // digit-first mode, otherwise whatever sits in the selected cell.
    const activeDigit =
      selection.digit ??
      (selection.cell !== null && game.valueAt(selection.cell) !== EMPTY
        ? (game.valueAt(selection.cell) as Digit)
        : null);

    const peers =
      selection.cell !== null && settings.highlightPeers
        ? new Set(CLASSIC.peers[selection.cell]!)
        : null;

    for (let cell = 0; cell < CELLS; cell++) {
      const parts = this.cells[cell]!;
      const value = game.valueAt(cell);
      const given = game.isGiven(cell);

      parts.value.textContent = value === EMPTY ? "" : String(value);

      const cornerMask = game.marksAt(cell, "corner");
      const centerMask =
        value === EMPTY && auto ? auto[cell]! | game.marksAt(cell, "center") : game.marksAt(cell, "center");

      parts.corner.replaceChildren();
      if (value === EMPTY && cornerMask) {
        for (const digit of digitsOf(cornerMask)) {
          const pip = document.createElement("i");
          pip.textContent = String(digit);
          pip.style.gridArea = `p${digit}`;
          parts.corner.append(pip);
        }
      }

      if (value === EMPTY && centerMask) {
        const digits = digitsOf(centerMask);
        parts.center.textContent = digits.join("");
        parts.center.dataset.count = String(digits.length);
      } else {
        parts.center.textContent = "";
        delete parts.center.dataset.count;
      }

      const sameDigit =
        settings.highlightSameDigit &&
        activeDigit !== null &&
        (value === activeDigit ||
          (value === EMPTY && (centerMask & maskOf(activeDigit)) !== 0) ||
          (value === EMPTY && (cornerMask & maskOf(activeDigit)) !== 0));

      const root = parts.root;
      root.classList.toggle("is-given", given);
      root.classList.toggle("is-entered", !given && value !== EMPTY);
      root.classList.toggle("is-selected", selection.cell === cell);
      root.classList.toggle("is-peer", peers?.has(cell) ?? false);
      root.classList.toggle("is-same-digit", Boolean(sameDigit));
      root.classList.toggle("is-conflict", conflicts.has(cell));
      root.classList.toggle("is-mistake", mistakes.has(cell));
      root.classList.toggle("is-hint", highlight.pattern?.has(cell) ?? false);
      root.classList.toggle("is-hint-focus", highlight.focus?.has(cell) ?? false);
      root.tabIndex = selection.cell === cell ? 0 : -1;
      root.setAttribute("aria-selected", String(selection.cell === cell));
      root.setAttribute("aria-label", describe(cell, value, given, cornerMask, centerMask, conflicts.has(cell)));
      root.setAttribute("aria-readonly", String(given));
    }
  }
}

function describe(
  cell: number,
  value: number,
  given: boolean,
  cornerMask: number,
  centerMask: number,
  conflict: boolean,
): string {
  const where = `Row ${rowOf(cell) + 1}, column ${colOf(cell) + 1}`;
  if (value !== EMPTY) {
    return `${where}, ${value}${given ? ", given" : ""}${conflict ? ", conflicts" : ""}`;
  }
  const notes = digitsOf(cornerMask | centerMask);
  return notes.length > 0
    ? `${where}, empty, notes ${notes.join(" ")}`
    : `${where}, empty`;
}
